/**
 * Singleton that manages a single microphone MediaStream.
 *
 * - Only one stream exists at a time.
 * - Concurrent `acquire()` calls are deduplicated (returns the same promise).
 * - `release()` stops all tracks and clears the reference.
 * - Handles the Safari HTTPS requirement for `navigator.mediaDevices`.
 */

/** Module-level singleton instance */
let instance: MicrophoneManager | null = null;

export class MicrophoneManager {
  /** The currently active microphone MediaStream, or null if released */
  private stream: MediaStream | null = null;

  /** In-flight getUserMedia promise used to deduplicate concurrent acquire() calls */
  private pending: Promise<MediaStream> | null = null;

  /** Set to true when release() is called during a pending acquire() */
  private releasedWhilePending = false;

  /**
   * Returns the singleton MicrophoneManager instance.
   * Creates one on first call; subsequent calls return the same instance.
   */
  static getInstance(): MicrophoneManager {
    instance ??= new MicrophoneManager();
    return instance;
  }

  /** Returns the current active stream, or null if not acquired. */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Acquires a microphone stream via getUserMedia.
   * If a stream already exists, returns it immediately.
   * Concurrent calls while getUserMedia is in-flight return the same promise.
   */
  async acquire(): Promise<MediaStream> {
    // Already have an active stream — verify tracks are still live
    if (this.stream) {
      const allLive = this.stream
        .getTracks()
        .every((t) => t.readyState === "live");
      if (allLive) {
        return this.stream;
      }
      // Tracks ended (e.g. user revoked permission) — release and re-acquire
      this.release();
    }

    // Deduplicate concurrent calls
    if (this.pending) {
      return this.pending;
    }

    this.releasedWhilePending = false;
    this.pending = this._requestStream();

    try {
      const stream = await this.pending;

      // If release() was called while we were awaiting getUserMedia,
      // stop the newly-acquired tracks immediately to avoid a mic leak.
      if (this.releasedWhilePending) {
        stream.getTracks().forEach((track) => track.stop());
        this.releasedWhilePending = false;
        throw new Error("Microphone released during acquisition");
      }

      this.stream = stream;
      return stream;
    } finally {
      this.pending = null;
    }
  }

  /**
   * Stops all tracks on the current stream and clears the reference.
   * Safe to call multiple times — no-ops if already released.
   */
  release(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    // If acquire() is in-flight, flag it so the stream is released on arrival
    if (this.pending) {
      this.releasedWhilePending = true;
    }
  }

  /** Whether a mic permission request is currently in-flight */
  isAcquiring(): boolean {
    return this.pending !== null;
  }

  /**
   * Internal: performs the actual getUserMedia call with audio constraints.
   * Handles browser-specific error messages for common failure cases.
   *
   * @throws {Error} If the browser doesn't support getUserMedia (e.g. HTTP on Safari).
   * @throws {Error} If the user denies microphone permission (NotAllowedError).
   * @throws {Error} If no microphone is found on the device (NotFoundError).
   */
  private async _requestStream(): Promise<MediaStream> {
    // Safari doesn't expose mediaDevices on non-secure (HTTP) origins
    if (!navigator.mediaDevices?.getUserMedia) {
      throw Object.assign(
        new Error(
          "Your browser does not support audio recording in this context. Please use HTTPS.",
        ),
        { name: "NotSupportedError" },
      );
    }

    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
        video: false,
      });
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));

      if (error.name === "NotAllowedError") {
        throw new Error(
          "Microphone permission denied. Please allow access in your browser settings.",
        );
      }
      if (error.name === "NotFoundError") {
        throw new Error("No microphone found on this device.");
      }
      throw error;
    }
  }
}
